import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parseFloatData } from "utils/UtilityFuncs";
import CardSizesCrud from "./CardSizesCrud";

export default function AnalysisCardCrud({
  setShowModal,
  isUpdateMode,
  formData,
  setFormData,
  refreshFunc,
  setIsUpdateMode,
}) {
  /* ******************** All States ************************* */
  const dispatch = useDispatch();

  const defaultFormState = {
    analysisCardNo: "",
    inputName: "",
    inputHsCode: "",
    outputName: "",
    outputHsCode: "",
  };

  const [loading, setLoading] = useState(false);
  const [sizeList, setSizeList] = useState([]);
  const [originalSizeList, setOriginalSizeList] = useState([]);

  /* ******************** All Functions ************************* */
  const resetForm = () => {
    setFormData(defaultFormState);
    setIsUpdateMode(false);
    setSizeList([]);
    setOriginalSizeList([]);
  };

  const buildCreatePayloadArray = () => {
    const commons = {
      analysisCardNo: formData.analysisCardNo,
      inputName: formData.inputName,
      inputHsCode: formData.inputHsCode,
      outputName: formData.outputName,
      outputHsCode: formData.outputHsCode,
    };

    return sizeList
      .filter(
        (e) =>
          e.size !== "" ||
          e.requirement !== "" ||
          e.useable !== "" ||
          e.wastage !== "" ||
          e.wastageRatio !== ""
      )
      .map((entry) => ({
        ...commons,
        sortId: entry.sortId || 1,
        analysisId: entry.analysisId || 1,
        size: entry.size,
        requirement:
          entry.requirement === "" || entry.requirement === null
            ? null
            : Number(parseFloatData(entry.requirement, 4)),
        useable:
          entry.useable === "" || entry.useable === null
            ? null
            : Number(parseFloatData(entry.useable, 4)),
        wastage:
          entry.wastage === "" || entry.wastage === null
            ? null
            : Number(parseFloatData(entry.wastage, 4)),
        wastageRatio:
          entry.wastageRatio === "" || entry.wastageRatio === null
            ? null
            : Number(parseFloatData(entry.wastageRatio, 4)),
      }));
  };

  const buildUpdatePayloadArrays = () => {
    const commons = {
      analysisCardNo: formData.analysisCardNo,
      inputName: formData.inputName,
      inputHsCode: formData.inputHsCode,
      outputName: formData.outputName,
      outputHsCode: formData.outputHsCode,
    };

    const normalizeNumber = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const parsed = parseFloatData(val, 4);
      if (parsed === null) return null;
      return Number(parsed);
    };

    const filteredSizes = sizeList.filter(
      (e) =>
        e.size !== "" ||
        e.requirement !== "" ||
        e.useable !== "" ||
        e.wastage !== "" ||
        e.wastageRatio !== ""
    );

    const addSizes = [];
    const updateSizes = [];

    filteredSizes.forEach((entry) => {
      const base = {
        ...commons,
        sortId: entry.sortId || 1,
        analysisId: entry.analysisId || 1,
        size: entry.size,
        requirement: normalizeNumber(entry.requirement),
        useable: normalizeNumber(entry.useable),
        wastage: normalizeNumber(entry.wastage),
        wastageRatio: normalizeNumber(entry.wastageRatio),
      };

      // New entry (no column id)
      if (!entry.id) {
        addSizes.push(base);
        return;
      }

      // Existing entry - include only if changed vs original
      const original = originalSizeList.find((o) => o.id === entry.id);
      if (!original) {
        // Safety: treat as new if somehow not in original list
        addSizes.push(base);
        return;
      }

      const isSizeSame = (original.size || "") === (entry.size || "");
      const isReqSame =
        normalizeNumber(original.requirement) ===
        normalizeNumber(entry.requirement);
      const isUseableSame =
        normalizeNumber(original.useable) === normalizeNumber(entry.useable);
      const isWastageSame =
        normalizeNumber(original.wastage) === normalizeNumber(entry.wastage);
      const isWastageRatioSame =
        normalizeNumber(original.wastageRatio) ===
        normalizeNumber(entry.wastageRatio);
      const isSortSame = (original.sortId || 1) === (entry.sortId || 1);
      const isAnalysisSame =
        (original.analysisId || 1) === (entry.analysisId || 1);

      const isUnchanged =
        isSizeSame &&
        isReqSame &&
        isUseableSame &&
        isWastageSame &&
        isWastageRatioSame &&
        isSortSame &&
        isAnalysisSame;

      if (!isUnchanged) {
        updateSizes.push({ ...base, id: entry.id });
      }
    });

    // IDs of sizes currently present after user edits (for rows that had an id)
    const currentIds = filteredSizes
      .map((e) => e.id)
      .filter((id) => id !== undefined && id !== null);

    // Sizes that existed originally but are now missing -> to be deleted
    const deleteSizes = originalSizeList
      .filter(
        (original) =>
          original.id !== undefined &&
          original.id !== null &&
          !currentIds.includes(original.id)
      )
      .map((original) => original.id);

    return { addSizes, updateSizes, deleteSizes, commonColumns: commons };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = buildCreatePayloadArray();

      setLoading(true);
      if (!payload.length) {
        dispatch(
          setNotification({
            message: "Please add at least one other column row.",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }
      const { data } = await API.post(
        "/manufacturer/efs-analysis-card",
        { sizesArray: payload },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        resetForm();
        setShowModal(false);
        await refreshFunc({ page: 1 });
        dispatch(
          setNotification({
            message: data?.message || "Created",
            type: "success",
          })
        );
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const { addSizes, updateSizes, deleteSizes, commonColumns } =
        buildUpdatePayloadArrays();
      console.log({ addSizes, updateSizes, deleteSizes, commonColumns });

      setLoading(true);

      //   if (!addSizes.length && !updateSizes.length) {
      //     dispatch(
      //       setNotification({
      //         message: "Please add at least one other column row.",
      //         type: "error",
      //       })
      //     );
      //     setLoading(false);
      //     return;
      //   }
      const { data } = await API.patch(
        "/manufacturer/efs-analysis-card",
        {
          addSizes,
          updateSizes,
          deleteSizes,
          commonColumns,
        },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        resetForm();
        setShowModal(false);
        await refreshFunc();
        dispatch(
          setNotification({
            message: data?.message || "Updated",
            type: "success",
          })
        );
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  /* ******************** All UseEffects ************************* */

  useEffect(() => {
    if (isUpdateMode && originalSizeList.length === 0 && sizeList.length > 0) {
      setOriginalSizeList(sizeList);
    }
  }, [isUpdateMode, sizeList, originalSizeList.length]);

  return (
    <div>
      <div className="mb-2">
        <h4>{isUpdateMode ? "Update Analysis Card" : "Add Analysis Card"}</h4>
      </div>
      <hr className="mt-0" />
      <form onSubmit={isUpdateMode ? handleUpdate : handleCreate} className="">
        <div className="row g-3">
          <div className="col-md-5">
            <Card className="h-100">
              <Card.Body>
                <Card.Title className="mb-3">Common Details</Card.Title>

                <div className="mb-2">
                  <label className="form-label">Analysis Card No</label>

                  <input
                    type="text"
                    className="form-control"
                    value={formData.analysisCardNo || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        analysisCardNo: e.target.value,
                      }))
                    }
                    placeholder="Enter Analysis Card No"
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label">Input Good Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.inputName || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inputName: e.target.value,
                      }))
                    }
                    placeholder="Enter Input Good Name"
                  />
                </div>

                <div className="mb-0">
                  <label className="form-label">Input HS Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.inputHsCode || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inputHsCode: e.target.value,
                      }))
                    }
                    placeholder="Enter Input HS Code"
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Output Good Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.outputName || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        outputName: e.target.value,
                      }))
                    }
                    placeholder="Enter Output Name"
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label">Output HS Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.outputHsCode || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        outputHsCode: e.target.value,
                      }))
                    }
                    placeholder="Enter Output HS Code"
                  />
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Sizes Crud */}

          <div className="col-md-7">
            <Card className="h-100">
              <Card.Body>
                <CardSizesCrud
                  isUpdateMode={isUpdateMode}
                  formData={formData}
                  sizeList={sizeList}
                  setSizeList={setSizeList}
                />
              </Card.Body>
            </Card>
          </div>
        </div>

        <div className="mt-3">
          <div className="d-flex justify-content-end">
            <button
              onClick={() => {
                setShowModal(false);
              }}
              className="btn btn-secondary btn-sm"
              type="button"
            >
              Close
            </button>
            <button
              disabled={loading}
              className="btn btn-primary btn-sm ms-1"
              type="submit"
            >
              {isUpdateMode ? "Update" : "Add"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
